import { useEffect, useRef, useState } from 'preact/hooks';
import { copyText } from '../lib/clipboard.js';
import { copyHttps } from '../screens/Done.js';
import {
  COPY_CLIPBOARD_COPY,
  COPY_LINK_CAUTION,
  COPY_LINK_CLIPBOARD_FAILED_COPY,
  COPY_PAYMENT_LINK_CAUTION,
  type CopyLinkOutcome,
  type CopyQuoteOutcome,
} from '../lib/actions.js';

/**
 * T524 — the manual-copy affordances (two until T660 M3, three since). ⚠️ T660
 * M2 MOVED THEM: they no longer
 * sit beside the customer's phone number on each host, they render inside the
 * SHARED delivery block (SendQuote's "Or copy it" group). See the T660 M2
 * paragraph below — this opening line is kept accurate deliberately, because a
 * reader who opens only this component would otherwise learn the pre-M2 contract.
 *
 * "Copy number" is pure client-side. ⚠️ It USED to be available on any booking,
 * and that is no longer true: living inside the delivery block means it inherits
 * that block's mount gate, so an un-quoted or parked booking no longer offers it.
 * That reverses a decision T524 recorded deliberately, so it is escalated to Alex
 * rather than settled here — see "Open for Alex" in
 * docs/plans/ecmb-kiosk-spine-and-delivery-plan.md and Q66 in
 * docs/OPEN-QUESTIONS.md. The number stays reachable either way: both hosts still
 * render it as a `tel:` link. "Copy message"
 * asks the server for the customer-facing quote message and puts THAT on the
 * clipboard, so Helen can paste both into WhatsApp by hand. Together they are
 * the whole feature for the phone-only customer who cannot be quoted by any
 * other route today (no email, and no WhatsApp customer-send driver).
 *
 * T660 M3 adds "Payment link only" (P0-3) beside them, and it is load-bearing
 * rather than a third convenience: with SMS vendor-blocked and email transport
 * unbuilt, these copy rows are the only delivery that functions at launch, and a
 * bare deposit URL could not be got onto Helen's clipboard by ANY means before
 * it. Its own gate, its own state, its own failure label - see the prop.
 *
 * WHAT THIS COMPONENT DELIBERATELY DOES NOT DO: render the composed message.
 * It goes from the response straight to the clipboard and is never put in the
 * DOM. That is a security property, not a layout preference. The feedback
 * widget's screenshot redaction blanks only the `value` ATTRIBUTE of form
 * controls (client/feedback/widget.ts) - child text nodes are cloned untouched -
 * so a message rendered into a <div> or <pre> would be captured verbatim into
 * the JPEG, live deposit URL and all, and `redactSensitive` cannot see a Stripe
 * link (its url rule needs a `?`, and https://buy.stripe.com/aBcDeF has none).
 * Not rendering it REMOVES the hole rather than mitigating it, and it matches
 * what was asked for: a copy button, not a preview. If a preview is ever wanted
 * it must be a <textarea>, so the value-blanking actually reaches it.
 *
 * BOTH GATES LIVE AT THE CALL SITE, not here. Each surface already has an
 * expression that decides whether a quote can be sent, and the copy affordance
 * must agree with it exactly; re-deriving that here would be a second opinion
 * about the same question. So the caller passes `null` to withhold a button.
 *
 * T660 M2 — the call site is now SendQuote, the ONE delivery block both Done and
 * BookingDetail host (P0-14). `phone` is that block's own `targetFor(sms)`, and
 * `onCopyMessage` is the handler each host hands it: App.tsx's re-price-guarded
 * wrapper for Done, a direct `copyQuoteMessage(bookingId)` for BookingDetail.
 * The two gates therefore still belong to the two hosts; what stopped being
 * duplicated is the MARKUP and the phone derivation.
 *
 * NO NEW CSS. Both buttons are the house `btn btn--quiet btn--inline` inside an
 * existing styled value cell, following CustomerInfoBlock's inline "Change"
 * button. This is a shared component rather than the same JSX written twice
 * because the tri-state, the clipboard call, the caution line and the failure
 * copy are identical on both surfaces - the markup it emits is what the two
 * sites would each have emitted.
 */
export type CopyAffordancesProps = {
  /**
   * The SERVER-OF-RECORD number, in the paste-ready E.164 form WhatsApp needs
   * (`+353871234567`), or null to withhold the button.
   *
   * Deliberately E.164 even though the surface beside it may display the local
   * form (`0871234567`): a number pasted into WhatsApp without the country code
   * does not resolve. The button says "Copy number" rather than naming a format
   * because the format is not Helen's problem - but the difference is real, so
   * it is stated here and in the spec rather than left to be discovered.
   */
  phone: string | null;
  /**
   * Fetch the composed message, or null to withhold the button. Null means "not
   * a quoted booking" (or, on the Done surface, a fixed-prop render with no
   * handler wired) - the same condition the surface uses for its send block.
   */
  onCopyMessage: (() => Promise<CopyQuoteOutcome>) | null;
  /**
   * T660 M3 (P0-3) - fetch this booking's live deposit URL, or null to withhold
   * the button.
   *
   * NOT gated on the phone, unlike the two above, and that is deliberate: their
   * gate exists because `POST .../copy-quote` refuses 409 `no_phone`, a rule the
   * payment-link route does not share. An email-only customer can be handed a
   * link by hand, so tying the money path to a phone would withhold the only
   * delivery that functions on a booking that can still be quoted.
   *
   * OPTIONAL, defaulting to null, following SendQuote's `channelLabel?` /
   * `onCopyMessage?` discipline: every existing direct render of this component
   * stays valid and gets exactly today's two buttons rather than a third one
   * wired to nothing.
   */
  onCopyPaymentLink?: (() => Promise<CopyLinkOutcome>) | null;
};

/**
 * The failure labels are 'Number not copied' / 'Message not copied' and NOT the
 * obvious 'Could not copy'. That string is already on this screen: Done.tsx's
 * deposit affordance uses it, and deposit-copy.spec.tsx asserts it by accessible
 * name. Two controls able to carry one name at the same time is a
 * multiple-match throw waiting for the day both fail - and Playwright matches
 * names by SUBSTRING, so the e2e net would see it too. Distinct labels also
 * happen to be more useful: they say WHICH thing did not copy.
 *
 * T660 M3 adds a THIRD: 'Payment link not copied'. It is neither a substring of
 * nor a superstring of the other two, and it does not contain 'Could not copy' -
 * which matters because Playwright matches accessible names by SUBSTRING while
 * testing-library matches a string name EXACTLY, so a name that is safe in one
 * harness can still be ambiguous in the other. The same check applies to the
 * idle label: 'Payment link only' cannot be reached by a query for 'Copy payment
 * link' (the retired banner's button) in either direction.
 */
type ButtonState = 'idle' | 'working' | 'copied' | 'failed';

/**
 * The hint ids each button POINTS AT with `aria-describedby` (T660 M3 review).
 *
 * Before this the three hints were bare sibling <span>s: a screen-reader user
 * got the button's own name change ('Payment link only' -> 'Payment link not
 * copied'), which conveys THAT it failed, but the REASON - including the keyless
 * "nothing has been charged" reassurance - was announced only if the user
 * happened to read on. On a money-adjacent control that is not good enough.
 *
 * There are FOUR ids and only three of them are refusal reasons: the number row
 * joined late (a second M3 review round found it failing bare, with no hint at
 * all to point at), and the caution is shared, pointed at by whichever row wrote
 * the clipboard.
 *
 * `aria-describedby` rather than `aria-live`, deliberately: a description is
 * read after the name and role, which is the right relationship for "here is why
 * that button says what it says", and it needs no region that could double-speak
 * with the name change. (The block's live-region work belongs to the milestone
 * that owns TotalBar.)
 *
 * MODULE-LEVEL CONSTANTS, following RateList's STRIP_ERROR_ID and Quote.tsx's
 * 'override-failure' - not generated ids. Two CopyAffordances can never share a
 * document: App.tsx switches on `state.screen`, so Done and BookingDetail are
 * alternatives, never siblings.
 */
const NUMBER_HINT_ID = 'copy-number-hint';
const MESSAGE_HINT_ID = 'copy-message-hint';
const LINK_HINT_ID = 'copy-link-hint';
const CLIPBOARD_CAUTION_ID = 'copy-clipboard-caution';

/**
 * The `aria-describedby` value for one row: the ids of the hints ACTUALLY
 * rendered for it, or undefined.
 *
 * `undefined` and never an empty string, because a dangling idref is worse than
 * no description at all - a screen reader that resolves nothing announces
 * nothing and the user is left with a name change and no reason, which is the
 * state this exists to fix, now with markup claiming otherwise.
 */
function describedBy(...ids: (string | false)[]): string | undefined {
  return ids.filter((id): id is string => id !== false).join(' ') || undefined;
}

export function CopyAffordances({
  phone,
  onCopyMessage,
  onCopyPaymentLink = null,
}: CopyAffordancesProps) {
  // ONE STATE PER BUTTON, never one shared 'copied' flag. Done.tsx's existing
  // `copied` useState is screen-scoped, and sharing it would relabel every copy
  // button on the screen at once - which, besides being wrong, reds
  // deposit-copy.spec.tsx through testing-library's multiple-match throw.
  const [numberState, setNumberState] = useState<ButtonState>('idle');
  /**
   * ...and the number row carries a REASON too (T660 M3 review, reproduced live
   * on two bookings). It used to be the only one of the three that failed bare:
   * 'Number not copied' and nothing else, while both siblings rendered a
   * `field-hint` saying why. Its failure mode is not special - it is a pure
   * client-side `writeText`, so the only way it fails is the clipboard, which is
   * exactly the case the message row already answers with COPY_CLIPBOARD_COPY.
   * Reused rather than reworded: a fourth near-duplicate sentence is what makes
   * substring-matched accessible names ambiguous in this repo, and the advice is
   * literally true here (the number IS on the screen, beside the button).
   */
  const [numberError, setNumberError] = useState<string | null>(null);
  const [messageState, setMessageState] = useState<ButtonState>('idle');
  const [messageError, setMessageError] = useState<string | null>(null);
  // T660 M3: and a third, for the same reason.
  const [linkState, setLinkState] = useState<ButtonState>('idle');
  const [linkError, setLinkError] = useState<string | null>(null);
  /**
   * H-12: what is on the clipboard that Helen should be careful with, or null.
   *
   * ONE piece of state holding the SENTENCE, not one boolean per button, and
   * T660 M3 is what forced the change: the OS clipboard holds exactly one thing,
   * so two independent flags could both be up at once and the screen would claim
   * the clipboard held a composed message AND a bare URL. Worse, the message
   * caution says the text has "personal details in it", which is false of the
   * URL that actually replaced it.
   *
   * Every successful write on every row assigns it - COPY_PAYMENT_LINK_CAUTION,
   * or COPY_LINK_CAUTION, or null for a message with no deposit line and null
   * for a bare number - so it tracks the CLIPBOARD rather than the click, in all
   * four directions. ALL THREE rows, including the number: a review round found
   * that one alone leaving the caution up over a clipboard holding nothing but a
   * phone number. Every failure branch returns BEFORE its assignment, so a later
   * refusal leaves the warning standing over the text that is genuinely still
   * there.
   *
   * It carries the ROW that wrote it as well as the sentence, and only so that
   * `aria-describedby` can point the right button at it: the caution is about
   * the clipboard, but it is announced as a description of the control that
   * loaded the clipboard, so a screen-reader user hears it on the button they
   * just pressed rather than on the other one. Kept as a stored tag rather than
   * re-derived by comparing the sentence to the two constants - that is the
   * "classify by message text" pattern SendQuote's `Failure` type exists to
   * avoid, and it would make rewording a caution silently move the description.
   */
  const [caution, setCaution] = useState<{ row: 'message' | 'link'; text: string } | null>(null);
  /**
   * Is this component still on screen?
   *
   * The copy path awaits a fetch (which itself may include a Stripe liveness
   * read) and only then writes the clipboard, so there is a real window in which
   * Helen taps New quote or Back and the component unmounts mid-flight. Every
   * setState after that is a harmless no-op - but the CLIPBOARD WRITE IS NOT.
   * Left unguarded it still runs, and the OS clipboard ends up holding another
   * customer's name, journey, total and a multi-use never-expiring payment URL
   * with nothing on screen having said so. That is exactly the wrong-thread
   * paste this feature's caution line exists to prevent, arrived at by a route
   * where the caution can never render.
   *
   * So the guard skips the WRITE, not the render: an unmounted component must
   * not silently load the clipboard. Screen state self-corrects on the next
   * render; the clipboard is an OS-level sink that outlives the app.
   */
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  /**
   * The OTHER rows' refusal lines, cleared because Helen has moved on (T660 M3
   * review).
   *
   * The three hint states are independent and each used to be cleared only by
   * its own handler, so failing "Copy message" and then succeeding on "Payment
   * link only" rendered TWO hint lines at once - one of them an error about a
   * button that is no longer the one in play. On a three-button row that is how
   * a stale refusal gets read as belonging to the tap that just happened.
   *
   * ERRORS ONLY. `caution` is deliberately untouched here (and each handler
   * still leaves its own alone until a successful write): it tracks the
   * CLIPBOARD rather than the click, so a new tap that has not yet written
   * anything must not wipe a warning about text that is genuinely still there.
   *
   * The other row's failed LABEL also stays ('Message not copied' remains that
   * button's own name until it is pressed again). It reports the outcome of the
   * last attempt on THAT button, which is still true; what was untrue was a
   * reason line sitting under a different button's fresh result.
   */
  function clearOtherErrors(row: 'number' | 'message' | 'link'): void {
    if (row !== 'number') setNumberError(null);
    if (row !== 'message') setMessageError(null);
    if (row !== 'link') setLinkError(null);
  }

  /**
   * The one row that touches no server: `copyText` and nothing else.
   *
   * The caution assignment is the part worth reading twice. A SUCCESSFUL number
   * copy replaces whatever was on the clipboard with a bare phone number, so a
   * payment-link caution raised by another row is now describing text that is no
   * longer there and must go. A FAILED one wrote nothing, so the link is still on
   * the clipboard and the caution must stay - which is why the assignment sits
   * after the write and behind the early return, exactly as the other two
   * handlers do it. Same contract, made uniform rather than excepted.
   */
  async function handleCopyNumber(): Promise<void> {
    if (phone === null) return;
    clearOtherErrors('number');
    if (!await copyText(phone)) {
      setNumberState('failed');
      setNumberError(COPY_CLIPBOARD_COPY);
      return;
    }
    setNumberState('copied');
    setNumberError(null);
    // A bare number carries no payment capability to misdirect, so this row
    // never RAISES a caution - it only retires the one it just overwrote.
    setCaution(null);
  }

  async function handleCopyMessage(): Promise<void> {
    if (onCopyMessage === null) return;
    setMessageState('working');
    setMessageError(null);
    clearOtherErrors('message');
    // NOTE what is deliberately NOT reset here: `caution`. Clearing it up front
    // reads as tidy and is wrong - every failure branch below returns before the
    // post-write assignment, so a later refusal (a 429, say) would clear the
    // warning while the clipboard STILL holds the cautioned text from the last
    // successful copy. The single assignment after a successful write moves the
    // flag in both directions, so it tracks the clipboard rather than the click.
    const outcome = await onCopyMessage();
    if (!outcome.ok) {
      setMessageState('failed');
      setMessageError(outcome.message);
      return;
    }
    // The clipboard write can still fail after a perfectly good response, and
    // Helen must not be told "copied" in that case - she would paste whatever
    // was there before, which on a shared kiosk is another customer's details.
    // The unmount guard: skip the write entirely rather than write-then-fail.
    if (!mounted.current) return;
    if (!await copyText(outcome.message)) {
      setMessageState('failed');
      setMessageError(COPY_CLIPBOARD_COPY);
      return;
    }
    setMessageState('copied');
    setCaution(outcome.depositIncluded ? { row: 'message', text: COPY_PAYMENT_LINK_CAUTION } : null);
  }

  /**
   * T660 M3 (P0-3) - the BARE deposit URL onto the clipboard, and nothing sent.
   *
   * Structurally handleCopyMessage, and every difference is deliberate:
   *  - `copyHttps`, NOT `copyText`. It opens with a `url.startsWith('https:')`
   *    check (SEC-9), which is a URL-SCHEME guard on the money path and the
   *    reason lib/clipboard.ts keeps the two helpers apart. A refusal there is
   *    indistinguishable from an unavailable clipboard at this seam, which is why
   *    the failure copy below covers both causes honestly instead of claiming the
   *    clipboard could not be reached (it may have been reachable and the URL
   *    refused, and there is nothing on screen to "read off" either way - the URL
   *    is never rendered).
   *  - the URL is a LOCAL. It never reaches the store and never reaches the DOM.
   *    Feedback-screenshot redaction blanks only the `value` ATTRIBUTE of form
   *    controls, and `redactSensitive` cannot recognise a Stripe link at all (its
   *    url rule needs a `?`, and https://buy.stripe.com/aBcDeF has none), so a
   *    rendered URL would be captured verbatim into a JPEG. Not rendering it
   *    REMOVES the hole rather than mitigating it.
   *  - the same unmount guard, for the same reason and a sharper one: what would
   *    land on the clipboard of an abandoned kiosk here is a payable URL.
   */
  async function handleCopyPaymentLink(): Promise<void> {
    if (onCopyPaymentLink === null) return;
    setLinkState('working');
    setLinkError(null);
    clearOtherErrors('link');
    // `caution` is deliberately NOT cleared here either - see handleCopyMessage.
    // A 429 from M1's per-booking `link:` throttle is a newly reachable refusal
    // on this button, and it must not wipe a warning about text that is still on
    // the clipboard.
    const outcome = await onCopyPaymentLink();
    if (!outcome.ok) {
      setLinkState('failed');
      setLinkError(outcome.message);
      return;
    }
    if (!mounted.current) return;
    if (!await copyHttps(outcome.url)) {
      // T879: the CLIPBOARD sentence, not the server one. Reaching here means the
      // action already returned ok - the link exists and the mint succeeded - so
      // the only thing that failed is this write. Sharing one sentence with the
      // server refusal is what made a dead Stripe key read as a clipboard fault.
      setLinkState('failed');
      setLinkError(COPY_LINK_CLIPBOARD_FAILED_COPY);
      return;
    }
    setLinkState('copied');
    setCaution({ row: 'link', text: COPY_LINK_CAUTION });
  }

  return (
    <>
      {phone !== null && (
        <button
          type="button"
          class="btn btn--quiet btn--inline"
          // The accessible name is this button's own TEXT, and it is static and
          // free of customer data by construction. That is the requirement, not
          // an accident: `describeTarget` (client/feedback/clicks.ts) prefers
          // `aria-label` and `redactSensitive` never catches names, so a control
          // labelled with a customer's number would ride into a committed
          // feedback draft. data-fb-opaque makes the click stream record THAT a
          // copy button was pressed, never for whom - the same treatment
          // Bookings.tsx's row buttons needed after exactly that leak.
          data-fb-opaque
          /* Its refusal reason, and never the caution: this row cannot put
             cautioned text on the clipboard, only clear it. */
          aria-describedby={describedBy(numberError !== null && NUMBER_HINT_ID)}
          onClick={() => { void handleCopyNumber(); }}
        >
          {numberState === 'copied' ? 'Number copied'
            : numberState === 'failed' ? 'Number not copied'
              : 'Copy number'}
        </button>
      )}
      {phone !== null && onCopyMessage !== null && ' '}
      {onCopyMessage !== null && (
        <button
          type="button"
          class="btn btn--quiet btn--inline"
          data-fb-opaque
          disabled={messageState === 'working'}
          /* The refusal reason, and the caution IF this row is what put the
             cautioned text on the clipboard. Both can be up at once: a later
             attempt can fail while the previous successful copy is still on the
             clipboard (pinned in copy-affordances.spec.tsx), so this is a LIST
             rather than a first-match. */
          aria-describedby={describedBy(
            messageError !== null && MESSAGE_HINT_ID,
            caution?.row === 'message' && CLIPBOARD_CAUTION_ID,
          )}
          onClick={() => { void handleCopyMessage(); }}
        >
          {messageState === 'working' ? 'Getting the message'
            : messageState === 'copied' ? 'Message copied'
              : messageState === 'failed' ? 'Message not copied'
                : 'Copy message'}
        </button>
      )}
      {(phone !== null || onCopyMessage !== null) && onCopyPaymentLink !== null && ' '}
      {/* T660 M3 (P0-3) - the row that makes a bare deposit URL reachable at all.
          Before this there was NO way for Helen to get one onto her clipboard:
          the two affordances on the Done banner are gated on `job.depositUrl`,
          whose only writer is the `sendDepositLink` action, which has no UI
          caller - so they never render in production.

          The name is 'Payment link only'. The 'only' is the feature: it says
          what this does NOT do, on a screen whose every other control sends
          something. It is also collision-free against the pins that keep the
          retired mint button retired ('Send deposit link' is not a substring of
          it) and against the retired banner's 'Copy payment link'. */}
      {onCopyPaymentLink !== null && (
        <button
          type="button"
          class="btn btn--quiet btn--inline"
          data-fb-opaque
          disabled={linkState === 'working'}
          /* The money row's own description. The keyless refusal lands here, so
             this is the wiring that makes "nothing has been charged" part of
             what a screen reader reads back with the button, not a line that
             may never be found. */
          aria-describedby={describedBy(
            linkError !== null && LINK_HINT_ID,
            caution?.row === 'link' && CLIPBOARD_CAUTION_ID,
          )}
          onClick={() => { void handleCopyPaymentLink(); }}
        >
          {linkState === 'working' ? 'Getting the payment link'
            /* 'Payment link ready to paste', NOT the obvious 'Payment link
               copied': that string CONTAINS the retired banner's 'Link copied'
               once case is folded, and Playwright matches accessible names by
               case-insensitive substring. The two can only share a screen on a
               hand-built `depositUrl` fixture today, but the banner is kept
               deliberately, and a name collision that only bites the day someone
               revives it is not a risk worth taking on the money path. It is
               also the more honest word: the link is on the clipboard, waiting -
               nothing was sent. */
            : linkState === 'copied' ? 'Payment link ready to paste'
              : linkState === 'failed' ? 'Payment link not copied'
                : 'Payment link only'}
        </button>
      )}
      {/* SPAN, not <p>. Done renders these affordances inside its own
          <p class="done-contact">, and a <p> nested in a <p> is invalid HTML -
          Preact builds the DOM node-by-node so it would survive there, but it
          would not survive the same markup being server-rendered or copied into
          a template later. A span is valid inside both that <p> and
          BookingDetail's <dd>, and .field-hint's colour and size (the house
          "check this" gold weight) apply to it unchanged, so this needs no new
          CSS either way.

          The refusal reason, in Helen's words. SEC-5: always one of the action's
          own constants, never a status code or a server sentence.

          The `id` is what the message button's `aria-describedby` resolves to,
          and it is rendered on the SAME condition the button points at it - the
          two must never disagree, or the description dangles. */}
      {/* The number row's own reason. Only ever COPY_CLIPBOARD_COPY today - the
          handler has one failure branch - but held as state like its siblings so
          the render stays one shape, and cleared by `clearOtherErrors` so it can
          never sit under another row's fresh result. That last part is not only
          tidiness: it and the message hint carry the SAME sentence, so two of
          them on screen at once would be a testing-library multiple-match throw
          as well as a lie about which button it belongs to. */}
      {numberError !== null && <>{' '}<span id={NUMBER_HINT_ID} class="field-hint">{numberError}</span></>}
      {messageError !== null && <>{' '}<span id={MESSAGE_HINT_ID} class="field-hint">{messageError}</span></>}
      {/* T660 M3: the payment-link row's own refusal line, and the branch a
          keyless kiosk always lands on. `DEPOSIT_KEYLESS_COPY` arrives here
          through the action's 503 mapping, so the phrase the net asserts
          verbatim reaches the screen without this component knowing why. */}
      {linkError !== null && <>{' '}<span id={LINK_HINT_ID} class="field-hint">{linkError}</span></>}
      {/* H-12. There is no technical control for the realistic harm here - the
          link is multi-use and never expires, and revoking it would kill it for
          the legitimate customer too - so the honest answer is to say so at the
          moment of the paste. Absent whenever the clipboard carries no payment
          capability to misdirect, and worded for whichever copy put it there. */}
      {caution !== null && <>{' '}<span id={CLIPBOARD_CAUTION_ID} class="field-hint">{caution.text}</span></>}
    </>
  );
}
